/**
 * Utility functions for handling @mentions in comment text
 */

/**
 * Parse comment content and return an array of text segments with mention markers
 *
 * @param {string} content - The comment text content
 * @returns {Array} Array of {type: 'text'|'mention', value: string} objects
 *
 * Example:
 *   parseContent("Hey @john, can you help?")
 *   Returns: [
 *     {type: 'text', value: 'Hey '},
 *     {type: 'mention', value: 'john'},
 *     {type: 'text', value: ', can you help?'}
 *   ]
 */
export const parseContentWithMentions = (content) => {
  if (!content) return [];

  const parts = [];
  const mentionPattern = /@([a-zA-Z][a-zA-Z0-9_-]*)/g;
  let lastIndex = 0;
  let match;

  while ((match = mentionPattern.exec(content)) !== null) {
    // Add text before the mention
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        value: content.substring(lastIndex, match.index)
      });
    }

    // Add the mention (without @ symbol)
    parts.push({
      type: 'mention',
      value: match[1]  // The username/teamname (without @)
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last mention
  if (lastIndex < content.length) {
    parts.push({
      type: 'text',
      value: content.substring(lastIndex)
    });
  }

  return parts;
};

/**
 * Render parsed content with React components
 * Returns an array of React elements
 */
export const renderContentWithMentions = (content) => {
  const parts = parseContentWithMentions(content);

  return parts.map((part, index) => {
    if (part.type === 'mention') {
      return (
        <span
          key={index}
          className="mention text-primary-600 font-medium bg-primary-50 px-1 rounded hover:bg-primary-100 cursor-pointer"
          title={`@${part.value}`}
        >
          @{part.value}
        </span>
      );
    }
    return <span key={index}>{part.value}</span>;
  });
};
